import { Player, AdPlayer, AdPlaybackPolicy, ImaCsaiSmartTvPlaybackConfig } from '@quickplay/player-interface';
import { createComposableAdPlayer } from './csai/ComposableAdPlayer';
import { createAdvancedCsaiComposableAdPlayer } from './csai/advanced/AdvancedCsaiComposableAdPlayer';
import { PlayerBuilder } from '../PlayerBuilder';
import type { AdFrequencyControlConfiguration } from '@quickplay/player';
import { IMAAdRequest } from '../models/csai/IMAAdRequest';
import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';
import { AdsRenderingSettings } from '../models/AdsRenderingSettings';
import { ImaSdkSettings } from '../models/ImaSdkSettings';
import { createSsaiComposableAdPlayer } from './ssai/SsaiComposableAdPlayer';

export function createPlayerBuilder(): PlayerBuilder {
  let player_: Player;
  let containerElement_: Element;
  let videoElement_: HTMLVideoElement;
  let imaSDK_: any;
  let request_: IMAAdRequest;
  let renderingSettings_: AdsRenderingSettings;
  let imaSdkSettings_: ImaSdkSettings;
  let defaultLogger: Logger = createLogger(LoggerLevel.OFF);
  let imaDaiStreamManager_: any;
  let adPlaybackPolicy_: AdPlaybackPolicy;
  let deferContentLoad_: boolean;
  let smartTvAdPlaybackOptions_: ImaCsaiSmartTvPlaybackConfig;
  let enablePrefetch_: boolean | undefined;
  let adFrequencyControlConfiguration_: AdFrequencyControlConfiguration | undefined;
  let isAfcEnabled: boolean = false;
  let configuredIncomingLeadTimeMs_: number | undefined;

  return {
    get player(): Player {
      return player_;
    },

    get containerElement(): Element {
      return containerElement_;
    },

    get videoElement(): HTMLVideoElement {
      return videoElement_;
    },

    get imaSDK(): any {
      return imaSDK_;
    },

    get configuredLogger(): Logger {
      return defaultLogger;
    },

    get request(): IMAAdRequest {
      return request_;
    },

    get renderingSettings(): AdsRenderingSettings {
      return renderingSettings_;
    },

    get imaSettings(): ImaSdkSettings {
      return imaSdkSettings_;
    },

    get imaDaiStreamManager(): any {
      return imaDaiStreamManager_;
    },

    get adPlaybackPolicy(): AdPlaybackPolicy {
      return adPlaybackPolicy_;
    },

    get deferContentLoad(): boolean {
      return deferContentLoad_;
    },

    get isPrefetchEnabled(): boolean | undefined {
      return enablePrefetch_;
    },

    get smartTvAdPlaybackOptions(): ImaCsaiSmartTvPlaybackConfig {
      return smartTvAdPlaybackOptions_;
    },

    get adFrequencyControlConfiguration(): AdFrequencyControlConfiguration | undefined {
      return adFrequencyControlConfiguration_;
    },

    get isAfcEnabled(): boolean {
      return isAfcEnabled;
    },

    get configuredIncomingLeadTimeMs(): number | undefined {
      return configuredIncomingLeadTimeMs_;
    },

    contentPlayer(player: Player): PlayerBuilder {
      player_ = player;
      return this;
    },

    contentContainerElement(element: Element): PlayerBuilder {
      containerElement_ = element;
      return this;
    },

    contentVideoElement(element: HTMLVideoElement): PlayerBuilder {
      videoElement_ = element;
      return this;
    },

    adRequest(request: IMAAdRequest): PlayerBuilder {
      request_ = request;
      return this;
    },

    ima(ima: any): PlayerBuilder {
      imaSDK_ = ima;
      return this;
    },

    adsRenderingSettings(renderingSettings: AdsRenderingSettings): PlayerBuilder {
      renderingSettings_ = renderingSettings;
      return this;
    },

    imaSdkSettings(settings: ImaSdkSettings): PlayerBuilder {
      imaSdkSettings_ = settings;
      return this;
    },

    logger(logger: Logger): PlayerBuilder {
      defaultLogger = logger;
      return this;
    },

    imaDaiManager(manager: any): PlayerBuilder {
      imaDaiStreamManager_ = manager;
      return this;
    },

    playbackPolicy(policy: AdPlaybackPolicy): PlayerBuilder {
      adPlaybackPolicy_ = policy;
      return this;
    },

    deferContentUntilAdLoad(deferContentload: boolean): PlayerBuilder {
      deferContentLoad_ = deferContentload;
      return this;
    },

    enablePrefetch(enablePrefetch: boolean): PlayerBuilder {
      enablePrefetch_ = enablePrefetch;
      return this;
    },

    adIncomingLeadTimeMs(leadTimeMs: number): PlayerBuilder {
      configuredIncomingLeadTimeMs_ = leadTimeMs;
      return this;
    },

    imaSmartTvAdPlaybackConfig(smartTvAdPlaybackOptions: ImaCsaiSmartTvPlaybackConfig): PlayerBuilder {
      smartTvAdPlaybackOptions_ = smartTvAdPlaybackOptions;
      return this;
    },

    adFrequencyControl(configuration?: AdFrequencyControlConfiguration): PlayerBuilder {
      isAfcEnabled = true;
      adFrequencyControlConfiguration_ = configuration;
      return this;
    },

    build(): AdPlayer {
      if (imaDaiStreamManager_) {
        return createSsaiComposableAdPlayer(this);
      }
      if (enablePrefetch_ !== undefined) {
        return createAdvancedCsaiComposableAdPlayer(this);
      }
      return createComposableAdPlayer(this);
    },
  };
}
